import { useCallback, useEffect, useState } from "react";
import { redirectIfPhonePe } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";
import { toast, Toaster } from "sonner";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, CheckCircle2, Loader2, Download, ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

export default function SupplierLicense() {
  const { user, api, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/seller/license/status");
      setState(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load license"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if ((user.account_type || user.role) !== "seller") return <Navigate to="/account" replace />;

  const pay = async (railChoice = "razorpay") => {
    setBusy(railChoice);
    try {
      const r = await api.post(`/seller/license/create-order?rail=${railChoice}`);
      if (redirectIfPhonePe(r.data)) return;
      const opts = {
        key: r.data.key_id,
        amount: r.data.amount * 100,
        currency: r.data.currency,
        name: "Vinci Inc.",
        description: "Annual Seller License",
        order_id: r.data.order_id,
        prefill: r.data.prefill,
        theme: { color: "#D8B23E" },
        handler: async (rp) => {
          try {
            await api.post("/seller/license/verify", {
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
            });
            toast.success("License activated · dashboard unlocked");
            await refresh(); load();
          } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      // Load checkout.js lazily
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej; document.body.appendChild(s);
        });
      }
      const rzp = new window.Razorpay(opts);
      rzp.open();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const r = await api.get("/seller/license/receipt/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "vinci-license-receipt.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download receipt"); }
  };

  const active = state?.active;

  return (
    <div className="min-h-screen bg-[#050506] text-[#F5F5F0]">
      <Toaster theme="dark" richColors position="top-right" />
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <Link to="/account" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] mb-6">
          <ArrowLeft size={12}/> Back to account
        </Link>

        <div className="flex items-start gap-4 border-b border-white/10 pb-6 mb-8">
          <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
            <Shield size={20}/>
          </div>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Vinci Seller License</div>
            <h1 className="font-serif text-3xl mt-1">Pay to unlock the seller dashboard.</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              A one-time annual license unlocks daily quoting, RFQ inbox, Elite scrutiny and payout rails.
              Fee is inclusive of 18% GST · a branded receipt is issued immediately after payment.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-10 text-center">
            <Loader2 size={16} className="inline animate-spin mr-2"/> Loading license state…
          </div>
        ) : active ? (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="border border-[#3DBB84]/40 bg-[#3DBB84]/[0.05] rounded-sm p-6" data-testid="license-active">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={16} className="text-[#3DBB84]"/>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#3DBB84]">License · Active</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field k="Receipt" v={state.receipt_no}/>
              <Field k="Paid on" v={state.paid_at ? new Date(state.paid_at).toLocaleString("en-IN") : "—"}/>
              <Field k="Validity" v={state.validity_end ? `until ${new Date(state.validity_end).toLocaleDateString("en-IN", {day:"2-digit", month:"short", year:"numeric"})}` : "—"}/>
              <Field k="Days left" v={state.days_left != null ? `${state.days_left} days` : "—"}/>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={download} data-testid="license-download"
                      className="inline-flex items-center gap-2 rounded-sm bg-[#D8B23E] hover:bg-[#c5a233] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2.5">
                <Download size={12}/> Download GST receipt
              </button>
              <Link to="/supplier/dashboard" data-testid="go-to-dashboard"
                    className="inline-flex items-center gap-2 rounded-sm border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2.5">
                <Sparkles size={12}/> Open seller dashboard
              </Link>
            </div>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="license-locked">
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Fee breakdown</div>
              <div className="mt-4 space-y-2 font-body text-sm">
                <Row k="Annual license (12 months)" v={inr(state.base_inr)}/>
                <Row k={`GST @ ${state.gst_pct}%`} v={inr(state.gst_inr)}/>
                <div className="border-t border-white/10 pt-2 flex justify-between">
                  <span className="font-grotesk text-[11px] uppercase tracking-[0.22em] text-[#D8B23E]">Total payable</span>
                  <span className="font-serif text-2xl text-[#F5F5F0]">{inr(state.total_inr)}</span>
                </div>
              </div>
              <ul className="mt-6 space-y-2 font-body text-xs text-[#C9C9C2]">
                <li>· Unlocks Daily Pulse rate updates + RFQ inbox.</li>
                <li>· Enables Elite scrutiny application.</li>
                <li>· Includes branded GST receipt PDF.</li>
                <li>· Auto-locks after {state.validity_days} days (renewal reminder 30 days before).</li>
              </ul>
            </div>
            <div className="border border-[#D8B23E]/40 bg-[#D8B23E]/[0.04] rounded-sm p-6">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Ready to pay?</div>
              <div className="font-serif text-2xl mt-1">Razorpay or PhonePe — your call.</div>
              <p className="font-body text-sm text-[#8A8A82] mt-2">
                Cards, UPI and net-banking on both rails. Payment verified server-side · your dashboard unlocks the moment
                the gateway confirms.
              </p>
              <div className="mt-4" data-testid="pay-license-btn">
                <PayRailButtons testId="pay-license" onPay={pay} busy={busy} amountLabel={inr(state.total_inr)} />
              </div>
              {!state.razorpay_enabled && (
                <div className="mt-3 font-body text-[11px] text-[#E5484D]">
                  Razorpay is not configured on this environment. Contact vinci.connect@vincitradeasia.com to enable.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="border border-white/10 rounded-sm bg-black/30 p-3">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
      <div className="font-body text-sm text-[#F5F5F0] mt-1">{v}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#8A8A82]">{k}</span>
      <span className="text-[#F5F5F0]">{v}</span>
    </div>
  );
}
